import type { FC } from 'react';
import { BaseLineCirclePause } from '@/app/components/icons/BaseLineCirclePause';
import { BaseLinePlay2 } from '@/app/components/icons/BaseLineCirclePlay';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { cn } from '@/app/utils';

interface UploadedAudioCardProps {
  audioName: string;
  isPlaying?: boolean;
  playDisabled?: boolean;
  showPlayButton?: boolean;
  showRemoveButton?: boolean;
  className?: string;
  onSelectAudio?: () => void;
  onPlayPause?: () => void;
  onRemove?: () => void;
}

export const getAudioDisplayName = (audioSource?: string, fallbackName?: string): string => {
  const normalizedFallbackName = fallbackName?.trim();
  if (normalizedFallbackName) {
    return normalizedFallbackName;
  }

  if (!audioSource) {
    return '';
  }

  try {
    const parsedUrl = new URL(audioSource);
    return decodeURIComponent(parsedUrl.pathname.split('/').filter(Boolean).pop() || '');
  } catch {
    return decodeURIComponent(audioSource.split('?')[0]?.split('#')[0]?.split('/').filter(Boolean).pop() || '');
  }
};

export const UploadedAudioCard: FC<UploadedAudioCardProps> = ({
  audioName,
  isPlaying = false,
  playDisabled = false,
  showPlayButton = true,
  showRemoveButton = true,
  className,
  onSelectAudio,
  onPlayPause,
  onRemove,
}) => {
  const isSelectDisabled = !onSelectAudio;
  const isPlayButtonDisabled = playDisabled || !onPlayPause;
  const shouldShowRemoveButton = showRemoveButton && !!onRemove;

  return (
    <div className={cn('flex items-center gap-4 px-4', className)}>
      <div className="flex min-w-0 flex-1 items-center py-4">
        <div
          className={cn(
            'flex h-8 min-w-0 flex-1 items-center gap-1 rounded-8 bg-system-white12 pr-2 transition-colors',
            isPlaying && 'bg-system-white16',
          )}
        >
          {onSelectAudio ? (
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-8 text-left disabled:cursor-default"
              onClick={onSelectAudio}
              disabled={isSelectDisabled}
            >
              <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-4 border border-system-white24 bg-system-white">
                <BaseLineMusic className="size-4 text-system-black" />
              </div>
              <span className="min-w-0 flex-1 truncate text-system-white text-xs leading-[1.4]">{audioName}</span>
            </button>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-8 text-left">
              <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-4 border border-system-white24 bg-system-white">
                <BaseLineMusic className="size-4 text-system-black" />
              </div>
              <span className="min-w-0 flex-1 truncate text-system-white text-xs leading-[1.4]">{audioName}</span>
            </div>
          )}

          {shouldShowRemoveButton ? (
            <button
              type="button"
              className="flex size-5 shrink-0 items-center justify-center rounded-4 text-system-white/40 transition-colors hover:bg-system-white08 hover:text-system-white"
              onClick={onRemove}
            >
              <BaseLineClose className="size-3" />
            </button>
          ) : null}
        </div>
      </div>

      {showPlayButton ? (
        <button
          type="button"
          className="flex size-6 shrink-0 items-center justify-center text-system-white transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:text-system-white/20"
          onClick={onPlayPause}
          disabled={isPlayButtonDisabled}
        >
          {isPlaying ? <BaseLineCirclePause className="size-6" /> : <BaseLinePlay2 className="size-6" />}
        </button>
      ) : null}
    </div>
  );
};
